import { SecondaryButton, TableRow, Text } from '@tuhi/components'
import { AccForm, AccFormSection } from 'app/api/forms/dto/accFormDto'
import { FC, Suspense } from 'react'
import { styled } from 'styled-components'
import { useDispatch } from 'react-redux'
import { openModal } from 'app/store/slices/modalSlice'
import { EditSectionModalLazy as EditSectionModal } from '../modals/editSectionModal.lazy'
import { Loading, LottieAnimationName } from '@tuhi/components'
import { media } from '@tuhi/components/src/theme/breakpoints'

const Container = styled.div`
    display: flex;
    flex-direction: column;
`

const Header = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`

const Row = styled(TableRow)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    ${media.tabletAndBelow} {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const ValidationMessage = styled(Text)`
    margin-top: 2px;
`

const StrippedFieldMessage = styled.div`
    margin-top: 4px;
    padding: 4px 8px;
    background-color: #fef3cd;
    border-left: 3px solid #d97706;
    border-radius: 2px;
    font-size: ${(p) => 12 / p.theme.baseFontSize}em;
    line-height: 1.4;
    color: #92400e;
    font-weight: 500;
`

interface Props {
    id: string
    form: AccForm
    section: AccFormSection
    onSectionUpdated: (updatedSection: AccFormSection) => Promise<void>
    isInErrorCorrectionMode?: boolean
    emptyRequiredFields?: string[]
    strippedFields?: string[]
}

export const ReviewSection: FC<Props> = ({
    id,
    form,
    section,
    onSectionUpdated,
    isInErrorCorrectionMode = false,
    emptyRequiredFields = [],
    strippedFields = [],
}) => {
    const dispatch = useDispatch()

    const handleEdit = () => {
        dispatch(
            openModal({
                children: (
                    <Suspense
                        fallback={
                            <Loading
                                name={LottieAnimationName.LoadSpinner}
                                width="120px"
                                height="120px"
                                message="Loading..."
                            />
                        }
                    >
                        <EditSectionModal
                            section={section}
                            formType={form.type}
                            onSave={onSectionUpdated}
                        />
                    </Suspense>
                ),
            })
        )
    }

    const isFormComplete =
        form.status === 'Complete' && !isInErrorCorrectionMode

    return (
        <Container id={id}>
            <Header>
                <Text size="medium" $weight="bold">
                    {section.title}
                </Text>
                {!isFormComplete && (
                    <SecondaryButton
                        width="hug"
                        height="small"
                        onClick={handleEdit}
                    >
                        Edit
                    </SecondaryButton>
                )}
            </Header>

            {section.rows.map((row, index) => {
                return (
                    <Row
                        key={`row-${index}`}
                        $isOdd={index % 2 !== 0}
                        disabled={true}
                    >
                        {row.columns.map((column, index) => {
                            return (
                                <Column key={`column-${index}`}>
                                    {column.map((item) => {
                                        const isEmptyRequired =
                                            !!item.linkId &&
                                            emptyRequiredFields.includes(
                                                item.linkId
                                            )
                                        const isFieldEmpty =
                                            !item.content ||
                                            item.content.trim() === ''
                                        const isStrippedField =
                                            !!item.linkId &&
                                            strippedFields.includes(
                                                item.linkId
                                            ) &&
                                            isFieldEmpty
                                        return (
                                            <div>
                                                <Text
                                                    size="xSmall"
                                                    $weight="medium"
                                                >
                                                    {item.label}
                                                </Text>
                                                <Text
                                                    size="xSmall"
                                                    $weight="regular"
                                                >
                                                    {item.content}
                                                </Text>
                                                {isStrippedField && (
                                                    <StrippedFieldMessage>
                                                        AI could not determine a
                                                        valid code — please
                                                        select a value manually
                                                    </StrippedFieldMessage>
                                                )}
                                                {isEmptyRequired && (
                                                    <ValidationMessage
                                                        size="xxSmall"
                                                        $weight="medium"
                                                        color="error"
                                                    >
                                                        This field is required
                                                    </ValidationMessage>
                                                )}
                                            </div>
                                        )
                                    })}
                                </Column>
                            )
                        })}
                    </Row>
                )
            })}
        </Container>
    )
}
